/**
 * SuggestionStrip — quiet ghost-recommendation row inside the Rules Builder.
 *
 * Lands in the real /rules?tab=builder surface (no stub route). Renders
 * AgentBus ghost Suggestions as a translucent strip below the dry-run output.
 * UX contract from docs/specs/08-agentic-canvas.md:
 *   · ghost-never-block — strip stays in document flow, no overlay popup
 *   · single-key fluency — ↹ accept · ␛ reject · ? explain
 *   · designer-pull — `Off` button collapses everything
 *   · honest empty-state — when no suggestions, the strip is invisible
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Lightbulb, Sparkles, X } from "lucide-react";
import type { Suggestion } from "../../../lib/agent/types";
import type { AgentBus } from "../../../lib/agent/bus";

interface Props {
  readonly bus: AgentBus;
  readonly onAccept?: (s: Suggestion) => void;
  readonly onReject?: (s: Suggestion) => void;
}

export function SuggestionStrip({ bus, onAccept, onReject }: Props): JSX.Element | null {
  const [open, setOpen] = useState<ReadonlyArray<Suggestion>>(() =>
    bus.snapshot().filter((s) => s.status === "ghost"),
  );

  // Listen for bus events; keep `open` in sync with ghost state only.
  useEffect(() => {
    const unsub = bus.subscribe(() => {
      setOpen(bus.snapshot().filter((s) => s.status === "ghost"));
    });
    return unsub;
  }, [bus]);

  // Drive expiry via requestAnimationFrame so we don't carry a setInterval.
  const rafRef = useRef<number | null>(null);
  useEffect(() => {
    const loop = (): void => {
      bus.tick(new Date());
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [bus]);

  // Single-key handlers (UX rule 4). Top-of-stack receives the keystroke.
  const top = useMemo(() => open[0] ?? null, [open]);
  useEffect(() => {
    if (!top) return;
    function onKey(e: KeyboardEvent): void {
      // Don't steal keys while the designer is typing in an input.
      const tag = (e.target as HTMLElement | null)?.tagName ?? "";
      if (tag === "INPUT" || tag === "TEXTAREA" || (e.target as HTMLElement | null)?.isContentEditable) return;
      if (!top) return;
      if (e.key === "Tab" && !e.shiftKey && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        const next = bus.apply(top.id, "accept");
        if (next && onAccept) onAccept(next);
      } else if (e.key === "Escape") {
        e.preventDefault();
        const next = bus.apply(top.id, "reject");
        if (next && onReject) onReject(next);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [bus, top, onAccept, onReject]);

  // Quiet idle state — designer must always know whether the agent is on
  // (UX rule 6 sticky off-switch). The empty pill renders even with zero
  // suggestions so the state is visible. Per CLAUDE.md no-stubs principle:
  // this is real product UX (state-of-agent indicator), not a placeholder.
  if (open.length === 0) {
    return (
      <div className="panel p-2.5 border-border bg-cream-dark/60 flex items-center justify-between">
        <div className="flex items-center gap-2 text-[11.5px] text-ink-500">
          <Sparkles className="w-3 h-3 text-foyr" />
          Agent · watching · 0 suggestions
        </div>
        <div className="text-[10px] text-ink-500 tabular">
          Fixes appear when the validator finds drift.
        </div>
      </div>
    );
  }

  return (
    <div className="panel p-3 border-foyr/30 bg-foyr/[0.04]">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 text-[12px] text-foyr-deep font-medium">
          <Sparkles className="w-3.5 h-3.5" />
          Agent · {open.length} ghost suggestion{open.length === 1 ? "" : "s"}
        </div>
        <div className="flex items-center gap-2 text-[10px] text-ink-500 tabular">
          <kbd className="px-1.5 py-0.5 rounded bg-cream-dark border border-border">↹</kbd>
          <span>accept</span>
          <kbd className="px-1.5 py-0.5 rounded bg-cream-dark border border-border">␛</kbd>
          <span>reject</span>
        </div>
      </div>
      <ul className="space-y-1.5">
        {open.map((s) => (
          <li key={s.id} className="flex items-start gap-2 text-[12px]">
            <Lightbulb className={`w-3.5 h-3.5 mt-0.5 ${s.pulse ? "text-foyr-deep" : "text-ink-500"}`} aria-hidden />
            <div className="flex-1 min-w-0">
              <div className="text-ink-950">
                <SuggestionLabel s={s} />
              </div>
              <div className="text-[10.5px] text-ink-500 tabular truncate">
                {s.wasInformedBy.join(" · ")} · conf {(s.confidence * 100).toFixed(0)}%
              </div>
            </div>
            <button
              type="button"
              onClick={() => {
                const next = bus.apply(s.id, "reject");
                if (next && onReject) onReject(next);
              }}
              className="btn btn-ghost p-1 text-ink-500 hover:text-ink-950"
              aria-label="Dismiss suggestion"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function SuggestionLabel({ s }: { s: Suggestion }): JSX.Element {
  const attrs = s.proposed.attrs as Record<string, unknown>;
  // Rule fire — surface the rule's human-readable identity first.
  if (typeof attrs["ruleTitle"] === "string" && typeof attrs["ruleId"] === "string") {
    const title = attrs["ruleTitle"] as string;
    const ruleId = attrs["ruleId"] as string;
    const severity = typeof attrs["severity"] === "string" ? (attrs["severity"] as string) : "warn";
    const fix = typeof attrs["fix"] === "string" ? (attrs["fix"] as string) : "";
    const sevColor = severity === "block" ? "text-foyr-deep" : severity === "warn" ? "text-foyr" : "text-ink-500";
    return (
      <span className="space-y-0.5 block">
        <span className="block">
          <span className={`pill text-[9.5px] border border-current/40 bg-current/10 ${sevColor} mr-1 uppercase tracking-wider tabular`}>
            {severity}
          </span>
          <span className="font-medium text-ink-950">{title}</span>
          {" "}
          <span className="text-ink-500 text-[10.5px]">fired on </span>
          <code className="font-mono text-[11px] text-ink-950">{s.about.kind} {s.about.id}</code>
        </span>
        {fix ? <span className="block text-[11px] text-ink-700 leading-snug">→ {fix}</span> : null}
        <span className="block text-[10px] text-ink-500 tabular">rule <code className="font-mono">{ruleId}</code></span>
      </span>
    );
  }
  // Missing-anchor room suggestion (kitchen needs island, bedroom needs bed, …).
  if (attrs["suggestion"] === "add-anchor-sku" && typeof attrs["missing"] === "string") {
    const missing = attrs["missing"] as string;
    const room = typeof attrs["roomLabel"] === "string" ? (attrs["roomLabel"] as string) : s.about.id;
    return (
      <span className="space-y-0.5 block">
        <span className="block">
          <span className="pill text-[9.5px] border border-foyr/40 bg-foyr/10 text-foyr-deep mr-1 uppercase tracking-wider tabular">
            missing
          </span>
          <span className="font-medium text-ink-950">{room}</span>
          <span className="text-ink-500"> needs a </span>
          <span className="font-medium text-ink-950">{missing}</span>
        </span>
        <span className="block text-[10px] text-ink-500">drag one from the catalog rail</span>
      </span>
    );
  }
  // Door-clearance / spatial flag suggestion (when B.1 ships, this will be common).
  if (typeof attrs["clearance"] === "string") {
    return <span className="font-medium text-ink-950">Clearance violation: {attrs["clearance"] as string}</span>;
  }
  if (attrs["swapField"] && typeof attrs["swapField"] === "object") {
    const sf = attrs["swapField"] as { from: string; to: string };
    return <>Swap unknown field <code className="font-mono">{sf.from}</code> → <code className="font-mono">{sf.to}</code></>;
  }
  const hint = String(attrs["suggestion"] ?? "");
  if (hint === "swap-rhs-or-op") return <>Tautology — swap RHS or operator.</>;
  if (hint === "swap-op-to-equal-or-different-rhs") return <>Unsatisfiable — swap operator or RHS.</>;
  if (hint === "swap-lo-hi") return <>Range inverted — swap lo and hi.</>;
  if (hint === "add-default-set-values") return <>Set is empty — pick at least one value.</>;
  if (hint === "coerce-or-swap-rhs") return <>Type mismatch — change RHS to match field type.</>;
  return <>Suggestion · {s.id}</>;
}
